import { PluginKey } from '@databases/plugins';
import { isPluginEnabled } from '@databases/plugins/helper';
import { type DatabaseView } from '@databases/types/database/view';

export const emptyArr = Object.freeze([]) as never[];

export const emptyObj = Object.freeze({});

export const isApple = !!navigator.platform.match(/iphone|ipad|ipod|mac/i);

export const isAndroid = !!navigator.userAgent.match(/android/i);

/**
 * Returns the number of columns and column width that a CSS grid would have with the given parameters.
 * @param containerWidth The client width (width including padding but excluding scroll bar) of the element containing the grid layout
 * @param containerPadding The left and right padding of the element containing the grid layout
 * @param gridGap The spacing that will be applied between the columns
 * @param minColumnWidth The minimum width that the columns must have. The number of columns is chosen so that the columns
 *     have at least this width (unless the container itself is more narrow than this, in which case 1 is returned).
 */
export function calculateGridColumns({
  containerWidth,
  containerPadding = 0,
  gridGap = 0,
  minColumnWidth,
}: {
  containerWidth: number;
  containerPadding?: number | [number, number];
  gridGap?: number;
  minColumnWidth: number;
}): { columns: number; columnWidth: number } {
  const padding = Array.isArray(containerPadding)
    ? containerPadding
    : [containerPadding, containerPadding];
  const columns = Math.max(
    1,
    Math.ceil((containerWidth - padding[0] - padding[1] + gridGap) / minColumnWidth)
  );
  const columnWidth =
    (containerWidth - padding[0] - padding[1] + gridGap) / columns! - gridGap;
  return { columns, columnWidth };
}

/**
 * An unreachable case that helps ensure the capture of uncovered cases
 * by explictly making typescript error when used in conjunction
 * with a switch statement.
 */
export const unreachable = (value: never) => {
  throw new Error(`Not implemented: ${value}`);
};

/**
 * This method determines whether to show the entry prefix (numbering or selection checkbox), with the following logic:
 * 1. If the CELL_SELECTION plugin is enabled, always show the entry prefix.
 * 2. If the CELL_SELECTION plugin is not enabled, check the view's settings:
 *    - If showNumberedEntries is true, show the entry prefix.
 *    - If showNumberedEntries is false or undefined, do not show the entry prefix.
 */
export const shouldShowEntryPrefix = (view: DatabaseView): boolean => {
  return (
    isPluginEnabled(PluginKey.CELL_SELECTION) ||
    view?.settings?.layout?.showNumberedEntries
  );
};
